/**
 * Microsoft Clarity loader (heatmaps + session replay). Clarity sets cookies, so it's
 * consent-gated like GA — PublicAnalytics only calls loadClarity after analytics consent.
 * This is the standard Clarity snippet, refactored to be type-safe + idempotent.
 */
declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

const CLARITY_ATTR = 'data-clarity';

/**
 * Build the initial queue stub. Matches Microsoft's official boot shim
 * (`c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}`) — the queue
 * is read from `window.clarity.q` on every call rather than closure-captured,
 * so if the global gets swapped (by Clarity's real script on load, or by
 * `unloadClarity` below), queued calls still hit the current queue array.
 */
function makeShim(): NonNullable<Window['clarity']> {
  const shim = function (this: unknown, ...args: unknown[]) {
    const c = window.clarity;
    if (!c) return; // shouldn't happen — but crash-loud-not-silent
    if (!c.q) c.q = [];
    c.q.push(args);
  } as NonNullable<Window['clarity']>;
  shim.q = [];
  return shim;
}

export function loadClarity(projectId: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`script[${CLARITY_ATTR}]`)) return;

  if (!window.clarity) window.clarity = makeShim();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
  // Expose cross-origin script errors to window.onerror (and thus PostHog
  // Error Tracking) instead of the scrubbed "Script error." blank.
  script.crossOrigin = 'anonymous';
  script.setAttribute(CLARITY_ATTR, '1');
  document.head.appendChild(script);
}

export function unloadClarity(): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(`script[${CLARITY_ATTR}]`).forEach((el) => el.remove());
  // Do NOT `delete window.clarity`. Clarity's minified script sets up internal
  // timers, IntersectionObservers, and postMessage listeners that lazy-lookup
  // `window.clarity.q` when they fire. Removing the script tag stops future
  // network hits but does NOT cancel those already-scheduled callbacks — they
  // keep firing until the tab dies. If `window.clarity` is undefined when one
  // runs, we get "Cannot read properties of undefined (reading 'unshift')"
  // in PostHog Error Tracking (issue #019f3bed).
  //
  // Instead, swap in a fresh no-op shim so the callbacks find a defined global
  // with an empty queue — anything they push/unshift lands in a black hole.
  window.clarity = makeShim();
}
